import React, { useState } from "react";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^\+?[0-9][0-9 .\-()]{5,29}$/;

const initial = {
  firstname: "",
  lastname: "",
  company: "",
  phone: "",
  email: "",
  message: "",
  consent: false,
  website: "", // honeypot
};

const MessageForm = () => {
  const { t, lang } = useLanguage();
  const f = t.contact.form;

  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");

  const setField = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: "" }));
    if (serverError) setServerError("");
  };

  const validate = () => {
    const e = {};
    if (!values.firstname.trim()) e.firstname = f.errors.firstname;
    if (!values.lastname.trim()) e.lastname = f.errors.lastname;
    if (!PHONE_RE.test(values.phone.trim())) e.phone = f.errors.phone;
    if (!EMAIL_RE.test(values.email.trim())) e.email = f.errors.email;
    if (values.message.trim().length < 5) e.message = f.errors.message;
    if (!values.consent) e.consent = f.errors.consent;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, lang }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.success) {
        setSubmitted(true);
        setValues(initial);
      } else if (res.status === 429) {
        setServerError(f.errors.rateLimit);
      } else {
        setServerError(data.detail || f.errors.generic);
      }
    } catch (_err) {
      setServerError(f.errors.generic);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSubmitted(false);
    setValues(initial);
    setErrors({});
    setServerError("");
  };

  if (submitted) {
    return (
      <div className="mag-form-success" role="status" aria-live="polite">
        <div className="mag-form-success-icon">
          <CheckCircle2 size={44} />
        </div>
        <h3>{f.successTitle}</h3>
        <p>{f.successBody}</p>
        <button type="button" className="btn btn-outline" onClick={reset}>
          {f.newMessage}
        </button>
      </div>
    );
  }

  return (
    <form className="mag-form" onSubmit={handleSubmit} noValidate>
      <div className="mag-form-header">
        <h3>{f.heading}</h3>
        <p>{f.subheading}</p>
      </div>

      <div className="mag-form-grid">
        <div className="mag-field">
          <label htmlFor="c-firstname">
            {f.firstname} <span className="req">*</span>
          </label>
          <input
            id="c-firstname"
            type="text"
            autoComplete="given-name"
            value={values.firstname}
            onChange={(e) => setField("firstname", e.target.value)}
            aria-invalid={!!errors.firstname}
            className={errors.firstname ? "has-error" : ""}
            maxLength={100}
          />
          {errors.firstname && <span className="mag-field-error">{errors.firstname}</span>}
        </div>

        <div className="mag-field">
          <label htmlFor="c-lastname">
            {f.lastname} <span className="req">*</span>
          </label>
          <input
            id="c-lastname"
            type="text"
            autoComplete="family-name"
            value={values.lastname}
            onChange={(e) => setField("lastname", e.target.value)}
            aria-invalid={!!errors.lastname}
            className={errors.lastname ? "has-error" : ""}
            maxLength={100}
          />
          {errors.lastname && <span className="mag-field-error">{errors.lastname}</span>}
        </div>

        <div className="mag-field mag-field-wide">
          <label htmlFor="c-company">
            {f.company} <span className="mag-field-hint">{f.companyOptional}</span>
          </label>
          <input
            id="c-company"
            type="text"
            autoComplete="organization"
            value={values.company}
            onChange={(e) => setField("company", e.target.value)}
            maxLength={200}
          />
        </div>

        <div className="mag-field">
          <label htmlFor="c-phone">
            {f.phone} <span className="req">*</span>
          </label>
          <input
            id="c-phone"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            placeholder={f.phonePlaceholder}
            value={values.phone}
            onChange={(e) => setField("phone", e.target.value)}
            aria-invalid={!!errors.phone}
            className={errors.phone ? "has-error" : ""}
            maxLength={30}
          />
          {errors.phone && <span className="mag-field-error">{errors.phone}</span>}
        </div>

        <div className="mag-field">
          <label htmlFor="c-email">
            {f.email} <span className="req">*</span>
          </label>
          <input
            id="c-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={values.email}
            onChange={(e) => setField("email", e.target.value)}
            aria-invalid={!!errors.email}
            className={errors.email ? "has-error" : ""}
            maxLength={200}
          />
          {errors.email && <span className="mag-field-error">{errors.email}</span>}
        </div>

        <div className="mag-field mag-field-wide">
          <label htmlFor="c-message">
            {f.message} <span className="req">*</span>
          </label>
          <textarea
            id="c-message"
            rows={5}
            placeholder={f.messagePlaceholder}
            value={values.message}
            onChange={(e) => setField("message", e.target.value)}
            aria-invalid={!!errors.message}
            className={errors.message ? "has-error" : ""}
            maxLength={5000}
          />
          {errors.message && <span className="mag-field-error">{errors.message}</span>}
        </div>

        {/* Honeypot - hidden from users and screen readers */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-9999px",
            width: 1,
            height: 1,
            overflow: "hidden",
            opacity: 0,
          }}
        >
          <label htmlFor="c-website">Website (leave blank)</label>
          <input
            id="c-website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={values.website}
            onChange={(e) => setField("website", e.target.value)}
          />
        </div>
      </div>

      <div className={`mag-consent ${errors.consent ? "has-error" : ""}`}>
        <label htmlFor="c-consent">
          <input
            id="c-consent"
            type="checkbox"
            checked={values.consent}
            onChange={(e) => setField("consent", e.target.checked)}
            aria-invalid={!!errors.consent}
          />
          <span>{f.consent}</span>
        </label>
        {errors.consent && <span className="mag-field-error">{errors.consent}</span>}
      </div>

      {serverError && (
        <div className="mag-form-server-error" role="alert">
          {serverError}
        </div>
      )}

      <button type="submit" className="btn btn-primary btn-lg mag-form-submit" disabled={submitting}>
        {submitting ? (
          <>
            <Loader2 size={18} className="mag-spin" />
            {f.submitting}
          </>
        ) : (
          <>
            <Send size={18} />
            {f.submit}
          </>
        )}
      </button>
    </form>
  );
};

export default MessageForm;
